import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from '../../common-service';
import { environment } from '../../../../../environments/environment';
import { GetAllEmissorResponse } from '../../responses/dfe/get-all-emissor.response';
import { GetEmissorByIdResponse } from '../../responses/dfe/get-emissor-by-id.response';
import { EmissorRequest } from '../../requests/dfe/emissor.request';
import { ReturnStruct } from '../../structs/return.struct';
import { SalvarEmissorResponse } from '../../responses/dfe/salvar-emissor.response';
import { ListaEmissorElegivelResponse } from '../../responses/dfe/lista-emissor-elegivel.response';
import { DefinirPadraoNFeEmissorResponse } from '../../responses/dfe/definir-padrao-nfe-emissor.response';
import { ReservarNumeroNFeEmissorResponse } from '../../responses/dfe/reservar-numero-nfe-emissor.response';
import { ProximoNumeroNFeEmissorResponse } from '@shared/services/responses/dfe/proximo-numero-nfe-emissor.response';
import { ModeloDocumentoEnum } from '../../../enums/dfe/modelo-documento.enum';

@Injectable({
  providedIn: 'root',
})
export class EmissorService extends CommonService {
  constructor(
    private httpClient: HttpClient
  ) {
    super();
  }

  private readonly apiUrl = `${environment.urlApiDFe}Emissor`;

  /**
   * Obtém uma lista paginada de emissores.
   * Corresponde a: [HttpGet] /Emissor
   * @param request - Objeto com os parâmetros de paginação e filtro.
   */
  public getAll(textoBusca: string, paginaAtual: number): Observable<GetAllEmissorResponse> {

    let url = this.apiUrl  + `?textoBusca=${textoBusca}&paginaAtual=${paginaAtual}`;

    return this.httpClient
      .get<GetAllEmissorResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém um emissor específico pelo seu ID.
   * Corresponde a: [HttpGet] /Emissor/idEmissor/{idEmissor}
   * @param idEmissor - O ID do emissor a ser buscado.
   */
  public getById(idEmissor: number): Observable<GetEmissorByIdResponse> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}`;
    return this.httpClient
      .get<GetEmissorByIdResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cria um novo emissor.
   * Corresponde a: [HttpPost] /Emissor
   * @param request - O objeto com os dados do novo emissor.
   */
  public create(request: EmissorRequest): Observable<SalvarEmissorResponse> {

    return this.httpClient
      .post<SalvarEmissorResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Atualiza um emissor existente.
   * Corresponde a: [HttpPut] /Emissor/idEmissor/{idEmissor}
   * @param idEmissor - O ID do emissor a ser atualizado.
   * @param request - O objeto com os dados atualizados do emissor.
   */
  public update(idEmissor: number, request: EmissorRequest): Observable<SalvarEmissorResponse> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}`;
    return this.httpClient
      .put<SalvarEmissorResponse>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Realiza a exclusão lógica de um emissor.
   * Corresponde a: [HttpDelete] /Emissor/idEmissor/{idEmissor}
   * @param idEmissor - O ID do emissor a ser deletado.
   */
  public delete(idEmissor: number): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}`;
    return this.httpClient
      .delete<ReturnStruct>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Lista os emissores ativos com o modelo informado ativo, com série e ambiente efetivo.
   * Corresponde a: [HttpGet] /Emissor/elegiveis?modelo={modelo}
   */
  public getElegiveis(modelo: ModeloDocumentoEnum): Observable<ListaEmissorElegivelResponse> {
    const options = { ...this.addHeaderToken(), params: new HttpParams().set('modelo', modelo) };
    return this.httpClient
      .get<ListaEmissorElegivelResponse>(`${this.apiUrl}/elegiveis`, options)
      .pipe(catchError(this.handleError));
  }

  /**
   * Marca o emissor como padrão para NF-e e desmarca o anterior.
   * Corresponde a: [HttpPut] /Emissor/idEmissor/{idEmissor}/definir-padrao-nfe
   */
  public definirPadraoNFe(idEmissor: number): Observable<DefinirPadraoNFeEmissorResponse> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}/definir-padrao-nfe`;
    return this.httpClient
      .put<DefinirPadraoNFeEmissorResponse>(url, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Reserva o próximo número de NF-e do emissor (último sequencial mais um).
   * Corresponde a: [HttpPut] /Emissor/idEmissor/{idEmissor}/reservar-numero-nfe
   */
  public reservarNumeroNFe(idEmissor: number): Observable<ReservarNumeroNFeEmissorResponse> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}/reservar-numero-nfe`;
    return this.httpClient
      .put<ReservarNumeroNFeEmissorResponse>(url, {}, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Consulta o próximo número livre de NF-e do emissor, sem reservar (a reserva acontece ao salvar a nota).
   * Corresponde a: [HttpGet] /Emissor/idEmissor/{idEmissor}/proximo-numero-nfe
   */
  public obterProximoNumeroNFe(idEmissor: number): Observable<ProximoNumeroNFeEmissorResponse> {
    const url = `${this.apiUrl}/idEmissor/${idEmissor}/proximo-numero-nfe`;
    return this.httpClient
      .get<ProximoNumeroNFeEmissorResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
